import { useEffect, useState, useCallback } from 'react'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { UpgradeBanner } from '../components/ui/UpgradeBanner'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { useToast } from '../components/ui/Toast'

interface Commission {
  id: string
  user_id: string
  user_name?: string
  proposal_id: string
  proposal_title?: string
  rule_id: string | null
  amount: number
  status: string
  paid_at: string | null
  created_at: string
}

interface DashboardSalesperson {
  user_id: string
  user_name: string
  pending: number
  paid: number
  total: number
}

export default function Commissions() {
  const { enabled, loading: flagLoading, requiredPlan } = useFeatureFlag('commission_tracking')
  const { toastError } = useToast()

  const [commissions, setCommissions] = useState<Commission[]>([])
  const [dashboard, setDashboard] = useState<DashboardSalesperson[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      const [commRes, dashRes] = await Promise.all([
        api.get<{ data: Commission[] }>(`/commissions?${params}`).catch(() => ({ data: [] })),
        api.get<{ salespersons: DashboardSalesperson[] }>('/commissions/dashboard').catch(() => ({ salespersons: [] })),
      ])
      setCommissions(commRes.data || [])
      setDashboard(dashRes.salespersons || [])
    } catch (err) {
      toastError('Failed to load commissions', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, toastError])

  useEffect(() => {
    if (enabled) loadData()
  }, [enabled, loadData])

  if (flagLoading) return <TableSkeleton cols={5} />
  if (!enabled) return <UpgradeBanner feature="Commission Tracking" plan={requiredPlan} description="Track sales commissions per proposal with automatic calculation and payout tracking." />

  const totalPending = dashboard.reduce((sum, s) => sum + s.pending, 0)
  const totalPaid = dashboard.reduce((sum, s) => sum + s.paid, 0)

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Commissions</h1>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Pending', value: formatCurrency(totalPending), color: 'var(--accent)' },
          { label: 'Total Paid', value: formatCurrency(totalPaid), color: 'var(--ok)' },
          { label: 'Salespersons', value: String(dashboard.length), color: 'var(--info)' },
        ].map((s, i) => (
          <GlowCard key={s.label} style={{ padding: '12px 20px', minWidth: 100, textAlign: 'center', flex: 1 }} delay={100 + i * 80}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{s.label}</div>
          </GlowCard>
        ))}
      </div>

      {/* Per-salesperson breakdown */}
      {dashboard.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            By Salesperson
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {dashboard.map((sp, i) => (
              <GlowCard key={sp.user_id} style={{ padding: '14px 16px' }} delay={260 + i * 80}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>{sp.user_name}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span>Pending</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent)' }}>{formatCurrency(sp.pending)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  <span>Paid</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--ok)' }}>{formatCurrency(sp.paid)}</span>
                </div>
              </GlowCard>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input max-w-[160px]" style={{ fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {loading ? <TableSkeleton cols={5} /> : commissions.length === 0 ? (
        <EmptyState
          title="Commissions will appear as proposals are accepted"
          description="Commissions are auto-calculated when proposals are accepted."
        />
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Salesperson</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Proposal</th>
                <th className="text-right" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Amount</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Status</th>
                <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{c.user_name || '-'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.proposal_title || '-'}</td>
                  <td className="text-right" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 14, color: 'var(--accent-h)' }}>{formatCurrency(c.amount)}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
